// The sales-hold path choice (2026-10-27, docs/pipeline-behaviors.md "Sales
// hold: fix the address or contact the customer";
// docs/decisions/2026-10-27-sales-hold-path-choice.md).
//
// On an internal sales-hold notice whose branch can go either way (address
// validation, address too long, need physical address) the steps open with a
// choice: Fix the address, or Contact the customer. It is a task_path_choice
// step (kind 'sales_hold_path') with a preselection; every step below it
// carries its path (taskPath 'fix' or 'contact'), so only the chosen path's
// steps show, Process and Process all. Pure: no calls.

import { effectiveTaskPathChoice, taskPathChoiceStep } from './tasks.js'

export const SALES_HOLD_PATH_KIND = 'sales_hold_path'
export const SALES_HOLD_PATH_FIX = 'fix'
export const SALES_HOLD_PATH_CONTACT = 'contact'
export const SALES_HOLD_PATH_LABELS = Object.freeze({
  [SALES_HOLD_PATH_FIX]: 'Fix the address',
  [SALES_HOLD_PATH_CONTACT]: 'Contact the customer',
})
export const SALES_HOLD_PATH_OPTIONS = Object.freeze([
  Object.freeze({ value: SALES_HOLD_PATH_FIX, label: SALES_HOLD_PATH_LABELS[SALES_HOLD_PATH_FIX] }),
  Object.freeze({ value: SALES_HOLD_PATH_CONTACT, label: SALES_HOLD_PATH_LABELS[SALES_HOLD_PATH_CONTACT] }),
])

export function isSalesHoldPathChoice(step) {
  return step?.type === 'task_path_choice' && step?.params?.kind === SALES_HOLD_PATH_KIND
}

// The path in effect on a suggestion: { path, preselected, overridden } or
// null when it has no sales-hold path choice. preselected: the path the
// pipeline concluded; overridden: a person chose the other one.
export function salesHoldPathOf(suggestion) {
  const step = taskPathChoiceStep(suggestion?.proposedActions)
  if (!isSalesHoldPathChoice(step)) return null
  const preselected = step.params?.preselected?.value ?? null
  const effective = effectiveTaskPathChoice(suggestion)
  const path = effective?.choice ?? preselected
  return { path, preselected, overridden: Boolean(path && preselected && path !== preselected) }
}

// The decision's record of the path (Activity Log; the Audit agreement and
// the feedback snapshot read it): salesHoldPath 'fix' | 'contact' and
// whether it was the preselected one. null when there is no path choice.
export function salesHoldPathDecisionEntry({ suggestion, status = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const p = salesHoldPathOf(suggestion)
  if (!p?.path) return null
  return {
    service: 'CXHub', method: 'DECISION', phase: 'approve', at, actedBy,
    label: `Decided (${status ?? 'unknown'}) on the sales-hold path: ${SALES_HOLD_PATH_LABELS[p.path] ?? p.path}${p.overridden ? ` (the pipeline preselected ${SALES_HOLD_PATH_LABELS[p.preselected] ?? p.preselected})` : ' (preselected)'}`,
    salesHoldPath: p.path,
    salesHoldPathPreselected: !p.overridden,
  }
}

// The recorded decision's path: { path, preselected: boolean } or null.
export function salesHoldPathFrom(apiCalls) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.salesHoldPath === SALES_HOLD_PATH_FIX || c?.salesHoldPath === SALES_HOLD_PATH_CONTACT) {
      return { path: c.salesHoldPath, preselected: c.salesHoldPathPreselected === true }
    }
  }
  return null
}

// A step on a path that is not the one in effect (a hidden path's step).
export function isOffSalesHoldPath(action, proposedActions, apiCalls = []) {
  if (!action?.taskPath) return false
  const effective = effectiveTaskPathChoice({ modelMeta: { apiCalls }, proposedActions })
  return Boolean(effective?.choice) && effective.choice !== action.taskPath
}

// One line of an address, for a note or a picker: "line1, line2, City, ST
// zip". Fields: shippingAddress1/2, shippingCity, shippingRegion,
// shippingPostalCode.
export function addressOneLine(a) {
  if (!a) return ''
  return [a.shippingAddress1, a.shippingAddress2, [a.shippingCity, a.shippingRegion].filter(Boolean).join(', '), a.shippingPostalCode]
    .map((s) => (typeof s === 'string' ? s.trim() : s)).filter(Boolean).join(', ')
}

// The note when a person picks the address on Fix the address from an
// ambiguous address's candidates (Part 2: never the first candidate
// silently). candidate: { address, source: 'smarty' | 'on_file', index,
// count }.
export function chosenCandidateNote({ address, source = 'smarty', index = 0, count = 0 } = {}) {
  const where = source === 'on_file'
    ? 'kept the address on file'
    : `chose Smarty's candidate ${index + 1} of ${count}`
  return `The address on file matched more than one address; an agent ${where}: "${addressOneLine(address)}". Sales Hold (Address Validation) resolved.`
}

// A review reason on a contact with no reply (Part 4): nothing is sent from
// the reply box, so the reason says "before processing", never "before
// sending".
export function reviewReasonForNoReply(reason, { hasReply = false } = {}) {
  if (!reason || hasReply) return reason
  return String(reason).replace(/\bbefore sending\b/g, 'before processing')
}
